import { useMutation, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import type { CopyType } from "@steelbill/types";
import { api, requestBlob } from "@/lib/api";
import { endpoints, type ChallanDetail, type DocumentEnvelope } from "@/lib/endpoints";
import { challanKeys } from "@/features/challans/use-challan-list";

/**
 * One delivery challan, and the two acts that close it (§17.7, Rule 55 CGST).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THERE IS A PDF HOOK HERE NOW — {@link useChallanPdf}. This note used to say
 * there was none and that the absence was deliberate, and that was TRUE when it
 * was written. `GET /challans/:id/pdf` has since landed, in
 * `apps/api/src/routes/documents-pdf.ts` rather than in `routes/challans.ts` —
 * which is exactly why the note went stale unnoticed: everybody who checked
 * looked in the challan router.
 *
 * THE THREE MISSING LIFECYCLE ACTS ARE MISSING ON PURPOSE TOO. `INVOICED` is set
 * by raising the invoice, never by typing it — claiming it by hand would mark a
 * delivery billed with no bill behind it, which is exactly the blindness §17.7
 * exists to remove. `OPEN` is where a challan starts and there is no way back to
 * it. And there is no delete: cancelling is a status, the number stays consumed,
 * and challan serial ranges are declared in GSTR-1 (§17.4).
 * ═════════════════════════════════════════════════════════════════════════════
 */

/**
 * `GET /challans/:id` → the challan, lines and all.
 *
 * A 404 HERE IS AMBIGUOUS BY DESIGN and the caller must not resolve it into
 * words it cannot justify. Every challan route is behind `requireFlag("challans")`
 * (§18.3), which runs before `requireCap` and answers 404 rather than 403 so that
 * "forbidden" cannot leak that unreleased work exists — so a 404 means either "no
 * such challan" or "the feature is off", and `ApiError.isNotFound` deliberately
 * cannot tell them apart. Render the not-found / not-available branch; never "you
 * do not have permission", which is the very leak the 404 was chosen to avoid.
 */
export function useChallan(id: string | undefined): UseQueryResult<ChallanDetail> {
  return useQuery({
    queryKey: challanKeys.detail(id ?? ""),
    queryFn: async () => {
      const response = await api.get<DocumentEnvelope<ChallanDetail>>(endpoints.challan(id ?? ""));
      return response.data;
    },
    enabled: id !== undefined && id !== "",
    /*
     * Thirty seconds — a quotation's window, not an issued invoice's five
     * minutes. An issued invoice is immutable, so its cache may sit as long as
     * it likes. A challan is EDITABLE while OPEN and, more to the point, moves
     * under this screen without the screen causing it: someone marks the goods
     * returned on the other device, or an invoice is raised and the status flips
     * to INVOICED. Thirty seconds keeps re-opening a challan from the register
     * cheap while making a stale "Mark returned" button a thirty-second window
     * rather than a five-minute one. The server refuses the stale attempt with a
     * 409 either way; this is about not offering it.
     */
    staleTime: 30_000,
  });
}

/**
 * The challan as paper — fetched as BYTES, never navigated to.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * MIRRORS `useInvoicePdf`, INCLUDING THE `copy` ARGUMENT, and the argument is the
 * reason this is not the quotation's simpler version. `useQuotationPdf` drops it
 * on purpose — a quotation has no statutory copies — but Rule 55(2) gives a
 * challan three, and until this hook existed there was NO WAY in the app to print
 * the DUPLICATE the driver is legally required to hold. §17.7 gives each of them
 * a caption naming who holds it: `(Original for Consignee)`, `(Duplicate for
 * Transporter)`, `(Triplicate for Consigner)`.
 *
 * `CopyType` COMES FROM `@steelbill/types`, the enum `CopyTypeSchema` validates
 * the query against — not an inline union. `useInvoicePdf` spells its own out
 * literally; a second literal here would be the third copy of a three-member list
 * whose members are fixed by statute.
 *
 * WHY NOT `<a href="/api/v1/challans/12/pdf">`. The route is authenticated with a
 * BEARER token (§1.1, so the Capacitor shell works unchanged). A browser
 * navigation sends no Authorization header and renders the API's 401 JSON inside
 * a PDF viewer. The only way to open it is to fetch it with the header and hand
 * the bytes to the viewer as an object URL.
 *
 * EVERY OBJECT URL IS REVOKED — the previous one before a new one is made, and
 * the last one on unmount. `URL.createObjectURL` pins the whole blob until it is
 * revoked or the tab closes.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function useChallanPdf(id: string | undefined, number: string | undefined) {
  const [url, setUrl] = useState<string | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  const release = () => {
    if (urlRef.current !== null) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  };

  useEffect(() => release, []);

  const mutation = useMutation({
    mutationFn: async (copy?: CopyType) => {
      /*
       * NO `copy` MEANS ORIGINAL, and it is the SERVER that decides that. Unlike
       * an invoice there is no `copy_type` column on a challan to fall back to,
       * so the route's own default is the only answer — sending `?copy=ORIGINAL`
       * from here would be a second copy of that rule with nothing to gain.
       */
      const suffix = copy === undefined ? "" : `?copy=${copy}`;
      // `CHL/26-27/7` is not a filename. The number already carries its prefix.
      const fallback = `${(number ?? `challan-${id ?? ""}`).replace(/\//g, "-")}.pdf`;
      return requestBlob(`${endpoints.challanPdf(id ?? "")}${suffix}`, {
        fallbackFilename: fallback,
      });
    },
    onSuccess: (result) => {
      release();
      const next = URL.createObjectURL(result.blob);
      urlRef.current = next;
      setUrl(next);
      setFilename(result.filename);
    },
  });

  return { ...mutation, url, filename, release };
}

/** What `POST /challans/:id/return` accepts — `ChallanReturnSchema`, `.strict()`. */
export interface ChallanReturnInput {
  /**
   * `YYYY-MM-DD`, when the goods ACTUALLY came back — routinely not today. The
   * lorry returned on Saturday and the entry is made on Monday. Omitted means
   * now, which is the server's default and not something to fill in here.
   */
  returnedAt?: string;
}

/**
 * `POST /challans/:id/return` — "the goods came back".
 *
 * `challan.edit`, NOT a capability of its own: marking goods returned is
 * recording what happened to a document the same person may already revise
 * wholesale, and §2.1's whole point is that the map stays readable.
 *
 * THE SERVER REFUSES MORE THAN THIS HOOK CAN SEE, with a 409 and a sentence
 * written for the owner. Goods that were never marked returnable cannot come
 * back; a RETURNED challan is already closed; an INVOICED one is closed by a tax
 * document and the correction is a credit note; a CANCELLED one has nothing to
 * bring back. The caller should HIDE the button unless the challan is OPEN and
 * `isReturnable` — a greyed control invites hunting for the way to un-grey it —
 * and must still surface the 409 text when the state moved underneath.
 *
 * Invalidate the detail AND the register afterwards: the register shows status,
 * and `overdueOnly` stops matching a returned challan however late it was.
 */
export function useMarkChallanReturned(id: string | undefined) {
  return useMutation({
    mutationFn: async (input: ChallanReturnInput) => {
      const response = await api.post<DocumentEnvelope<ChallanDetail>>(
        endpoints.challanReturn(id ?? ""),
        input,
      );
      return response.data;
    },
  });
}

/**
 * `POST /challans/:id/cancel { reason }`.
 *
 * A STATUS, NEVER A DELETE, AND THE NUMBER STAYS CONSUMED. Challan serial ranges
 * are declared in GSTR-1 (§17.4), so a missing CHL/26-27/7 is a question from the
 * department — the same reasoning that makes an invoice cancellable but never
 * deletable (§5).
 *
 * THE REASON IS MANDATORY, and it is written to the `cancel_reason` COLUMN as
 * well as to the audit row. `audit_log` is an ADMIN surface (`audit.view`, §2.1)
 * that the owner cannot open; if he is ever to be told why the challan in front of
 * him was cancelled, it has to be on the challan.
 *
 * `ChallanCancelSchema` IS `.min(1)`, NOT `.min(3)`. The quotation void and the
 * invoice cancel both take 3–500 characters and their dialogs guard with
 * `reason.trim().length < 3`; copying that guard here is safe because it is
 * STRICTER than the API, but do not write a comment claiming the server enforces
 * three. `maxLength={500}` on the textarea is real on all three.
 *
 * A BILLED CHALLAN CANNOT BE CANCELLED — the server answers 409 naming the
 * invoice, because a tax document depends on this movement and that document
 * cannot be edited (§17.3).
 */
export function useCancelChallan(id: string | undefined) {
  return useMutation({
    mutationFn: async (reason: string) => {
      const response = await api.post<DocumentEnvelope<ChallanDetail>>(
        endpoints.challanCancel(id ?? ""),
        { reason },
      );
      return response.data;
    },
  });
}
